/* Offline-first diary entries: enqueue → IndexedDB → ordered upload (entry, then photos) */
import { dbPut, dbGet, dbAll, dbDelete, hasIndexedDb } from "./db";
import { resizeImage } from "./images";

const EVT = "diary-queue-changed";
const notify = () => window.dispatchEvent(new Event(EVT));
export const DIARY_QUEUE_EVENT = EVT;

const uuid = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

export async function enqueueDiary(jobId, payload, files = []) {
    const photos = [];
    for (const f of files) {
        const small = await resizeImage(f);
        photos.push({ id: uuid(), name: small.name || f.name, type: small.type, blob: small, uploaded: false });
    }
    const item = {
        localId: uuid(), jobId, seq: Date.now() + Math.random(), createdAt: new Date().toISOString(),
        payload: { ...payload, client_id: uuid() }, photos, status: "pending", error: "", serverId: null,
    };
    await dbPut(item);
    notify();
    return item;
}

export async function getDiaryQueue(jobId) {
    if (!hasIndexedDb()) return [];
    const all = await dbAll();
    return jobId ? all.filter((i) => i.jobId === jobId) : all;
}

export async function pendingDiaryCount() {
    if (!hasIndexedDb()) return 0;
    return (await dbAll()).filter((i) => i.status !== "sent").length;
}

export async function retryDiary(localId) {
    const item = await dbGet(localId);
    if (!item) return;
    item.status = "pending"; item.error = "";
    await dbPut(item);
    notify();
    return flushDiaryQueue();
}

export async function removeDiary(localId) {
    await dbDelete(localId);
    notify();
}

let flushing = false;

export async function flushDiaryQueue() {
    if (flushing || !hasIndexedDb()) return { sent: 0, failed: 0, remaining: 0 };
    if (!navigator.onLine) return { sent: 0, failed: 0, remaining: await pendingDiaryCount() };
    flushing = true;
    let sent = 0, failed = 0;
    try {
        const api = (await import("./api")).default;
        const items = (await dbAll()).filter((i) => i.status === "pending" || i.status === "sending");
        for (const item of items) {
            item.status = "sending"; item.error = "";
            await dbPut(item); notify();
            try {
                if (!item.serverId) {
                    const { data } = await api.post(`/jobs/${item.jobId}/diary`, item.payload, { _replay: true });
                    item.serverId = data.id;
                    await dbPut(item);
                }
                for (const p of item.photos) {
                    if (p.uploaded) continue;
                    const fd = new FormData();
                    fd.append("file", p.blob, p.name || "foto.jpg");
                    await api.post(`/jobs/${item.jobId}/diary/${item.serverId}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" }, _replay: true });
                    p.uploaded = true;
                    await dbPut(item);
                }
                item.status = "sent"; item.sentAt = new Date().toISOString();
                await dbPut(item); notify();
                sent += 1;
            } catch (e) {
                const offline = !e.response || e.response.status === 503;
                if (offline) {
                    item.status = "pending";
                    await dbPut(item); notify();
                    break;
                }
                item.status = "error";
                const d = e.response?.data?.detail;
                item.error = typeof d === "string" ? d : `Server odmítl záznam (${e.response.status})`;
                await dbPut(item); notify();
                failed += 1;
            }
        }
        await purgeSent();
    } finally {
        flushing = false;
        notify();
    }
    return { sent, failed, remaining: await pendingDiaryCount() };
}

async function purgeSent() {
    const cutoff = Date.now() - 24 * 60 * 60 * 1000;
    for (const i of await dbAll()) {
        if (i.status === "sent" && i.sentAt && new Date(i.sentAt).getTime() < cutoff) await dbDelete(i.localId);
    }
}
